import type { MyWorkItem } from '../hooks/useMyWork';

/**
 * A job picker that offers casing jobs AND non-casing work items in one
 * <select>.
 *
 * Casing options keep their plain job id as the value, exactly as before, so a
 * casing selection submits `jobId` / `job_id` unchanged. A work item's value is
 * prefixed, so the two can never be confused and a work item can never be sent
 * as a job id (the API refuses both together: `work_item_and_job`).
 */
const PREFIX = 'wi:';

export function workItemValue(item: Pick<MyWorkItem, 'vertical' | 'id'>): string {
  return `${PREFIX}${item.vertical}:${item.id}`;
}

export type PickerSelection =
  | { kind: 'none' }
  | { kind: 'job'; jobId: string }
  | { kind: 'workItem'; vertical: string; id: string };

export function decodePickerValue(value: string | null | undefined, noneValues: string[] = []): PickerSelection {
  if (!value || noneValues.includes(value)) return { kind: 'none' };
  if (value.startsWith(PREFIX)) {
    const rest = value.slice(PREFIX.length);
    const i = rest.indexOf(':');
    if (i > 0) return { kind: 'workItem', vertical: rest.slice(0, i), id: rest.slice(i + 1) };
    return { kind: 'none' };
  }
  return { kind: 'job', jobId: value };
}

/** The picker-eligible PT work: the API says which items can be linked. */
export function linkableItems(items: MyWorkItem[] | undefined, vertical: string): MyWorkItem[] {
  return (items ?? []).filter((i) => i.linkable && i.vertical === vertical);
}

/**
 * The verticals whose work items a hand can CLOCK IN against (api
 * lib/work-item.ts assertWorkItemClockable). Earthworks joined with
 * feat/ew-employee-hours: an EWJ job the hand is crewed on, still open.
 */
export const CLOCK_LINKABLE_VERTICALS = ['pressure_testing', 'earthworks'] as const;

/** The clock-in picker's work items, for the verticals this tenant has. */
export function clockLinkableItems(
  items: MyWorkItem[] | undefined,
  hasVertical: (key: string) => boolean,
): MyWorkItem[] {
  return CLOCK_LINKABLE_VERTICALS.filter((v) => hasVertical(v)).flatMap((v) => linkableItems(items, v));
}

/** "PTJ-0006 — Acme Oil · Pad 4 · Active" — every part from the API, none invented. */
export function workItemLabel(item: MyWorkItem): string {
  return [item.number, item.title].filter(Boolean).join(' — ') + ` · ${item.statusLabel}`;
}

/**
 * The extra body keys a picker value adds on the safety doors (camelCase,
 * api#976): `{ workItemVertical, workItemId }` for a work item, and NOTHING
 * otherwise — so a casing or general selection produces the byte-identical
 * body it always did.
 */
export function workItemBodyFields(value: string | null | undefined): { workItemVertical?: string; workItemId?: string } {
  const sel = decodePickerValue(value);
  return sel.kind === 'workItem' ? { workItemVertical: sel.vertical, workItemId: sel.id } : {};
}

/** True when a picker value names a work item rather than a casing job. */
export function isWorkItemValue(value: string | null | undefined): boolean {
  return decodePickerValue(value).kind === 'workItem';
}
